/** Positions tab pane — flat list of every open position across accounts. */
import { Loader2, RefreshCw, XCircle } from 'lucide-react';

export default function PositionsPane({ data, accounts, isAr, onCloseOne, closingId, onRefresh }) {
  const rows = [];
  data.accounts?.forEach((accBlock) => {
    const accInfo = accounts.find((a) => a.id === accBlock.account_id);
    accBlock.positions?.forEach((p) => rows.push({ ...p, accountId: accBlock.account_id, accountLabel: accInfo?.label || accBlock.account_id }));
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-white/50">
          {isAr ? `${rows.length} صفقة مفتوحة (يحدَّث كل 5 ثوانٍ)` : `${rows.length} open position(s) — auto-refresh 5s`}
        </span>
        <button
          onClick={onRefresh}
          className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white text-xs"
          data-testid="positions-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-10 text-white/40 text-sm">
          {isAr ? 'لا توجد صفقات مفتوحة' : 'No open positions'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Column headers — same 12-col grid as the rows so the labels line
              up perfectly under each value. Sticky-ish design so the trader
              can read at a glance which column is what. */}
          <div
            className="grid grid-cols-12 items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-[10px] font-black uppercase tracking-wider text-white/50"
            data-testid="positions-header"
          >
            <div className="col-span-2">{isAr ? 'الحساب' : 'Account'}</div>
            <div className="col-span-2">{isAr ? 'الرمز والنوع' : 'Symbol · Type'}</div>
            <div className="col-span-1 text-right">{isAr ? 'اللوت' : 'Lot'}</div>
            <div className="col-span-2 text-right">{isAr ? 'سعر الفتح' : 'Open price'}</div>
            <div className="col-span-2 text-right">{isAr ? 'السعر الحالي' : 'Current'}</div>
            <div className="col-span-2 text-right">{isAr ? 'الربح/الخسارة' : 'P&L ($)'}</div>
            <div className="col-span-1 text-right">{isAr ? 'إجراء' : '—'}</div>
          </div>
          {rows.map((p) => {
            const pnl = Number(p.profit || 0);
            const pnlColor = pnl >= 0 ? 'text-emerald-400' : 'text-red-400';
            return (
              <div
                key={`${p.accountId}-${p.broker_id}`}
                className="grid grid-cols-12 items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] text-xs"
                data-testid={`position-row-${p.broker_id}`}
              >
                <div className="col-span-2 min-w-0">
                  <div className="font-bold truncate">{p.accountLabel}</div>
                  <div className="text-[10px] text-white/40 font-mono truncate">#{p.broker_id}</div>
                </div>
                <div className="col-span-2 font-mono">
                  <span className="font-bold">{p.symbol}</span>
                  <span className={`ml-1.5 px-1 rounded text-[10px] ${p.type === 'buy' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                    {p.type.toUpperCase()}
                  </span>
                </div>
                <div className="col-span-1 text-right font-mono">{Number(p.volume).toFixed(2)}</div>
                <div className="col-span-2 text-right font-mono text-white/70">
                  {Number(p.open_price).toFixed(5)}
                </div>
                <div className="col-span-2 text-right font-mono text-white/90">
                  {p.current_price != null ? Number(p.current_price).toFixed(5) : '—'}
                </div>
                <div className={`col-span-2 text-right font-bold font-mono ${pnlColor}`}>
                  {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => onCloseOne(p.accountId, p.broker_id)}
                    disabled={closingId === p.broker_id}
                    className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-40"
                    title={isAr ? 'إغلاق' : 'Close'}
                    data-testid={`close-pos-${p.broker_id}`}
                  >
                    {closingId === p.broker_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <XCircle className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
